import React, { useState, useEffect, useRef } from "react";
import { Search, Mail, User, Clock, Send, CheckCircle, AlertCircle, Inbox, Filter, Check } from "lucide-react";
import AdminSidebar from "../../components/admin/AdminSidebar";
import axios from "axios";

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

export default function AdminSupport() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replying, setReplying] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // 'all', 'pending', 'replied'
  const [statusMsg, setStatusMsg] = useState(null); // { type: 'success' | 'error', text: string }

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    try {
      const res = await axios.get(`${apiUrl}/api/admin/support`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTickets(res.data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
      setStatusMsg({
        type: "error",
        text: "Failed to load support tickets. Ensure you are authorized."
      });
    }
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !selectedTicket) return;

    setReplying(true);
    setStatusMsg(null);
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");

    try {
      const res = await axios.post(`${apiUrl}/api/admin/support/${selectedTicket.id}/reply`, {
        reply: replyText
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.emailError) {
        setStatusMsg({
          type: "error",
          text: "Reply saved, but notification email failed to send (check SMTP settings)."
        });
      } else {
        setStatusMsg({
          type: "success",
          text: `Reply successfully sent to ${selectedTicket.email}`
        });
      }

      setReplyText("");
      // Refresh the tickets list and update the selected ticket state
      const updatedTickets = tickets.map(t => {
        if (t.id === selectedTicket.id) {
          const updated = {
            ...t,
            status: "replied",
            reply: replyText,
            repliedAt: new Date().toISOString()
          };
          setSelectedTicket(updated);
          return updated;
        }
        return t;
      });
      setTickets(updatedTickets);
    } catch (err) {
      console.error(err);
      setStatusMsg({
        type: "error",
        text: err.response?.data?.error || "Failed to send email reply. Please check SMTP settings."
      });
    } finally {
      setReplying(false);
    }
  };

  const insertTemplate = (text) => {
    let replacedText = text.replace("[Name]", selectedTicket?.name || "there");
    setReplyText(prev => prev + replacedText);
  };

  const filteredTickets = tickets.filter(ticket => {
    const matchesStatus = 
      statusFilter === "all" || 
      (statusFilter === "pending" && ticket.status === "pending") || 
      (statusFilter === "replied" && ticket.status === "replied");

    const matchesSearch = 
      (ticket.name && ticket.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      ticket.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.message.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesStatus && matchesSearch;
  });

  const templates = [
    { label: "Greeting", text: "Hi [Name],\n\nThank you for reaching out to WebHulu Support. " },
    { label: "FAQ Help", text: "You can easily post items or browse listings from our homepage. Let us know if you experience issues. " },
    { label: "Closing", text: "\n\nIf you have any other questions, feel free to reply directly to this email.\n\nBest regards,\nWebHulu Support" }
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex font-sans">
      <AdminSidebar />

      <main className="ml-64 flex-grow flex flex-col h-screen overflow-hidden">
        {/* HEADER */}
        <header className="p-8 pb-4 flex justify-between items-center bg-white border-b border-gray-100">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Support Center</h1>
            <p className="text-gray-500 text-sm">Read and reply to help requests directly via email.</p>
          </div>
          {statusMsg && (
            <div className={`px-4 py-3 rounded-xl flex items-center gap-2 text-xs font-semibold shadow-sm transition-all animate-fade-in ${
              statusMsg.type === "success" 
                ? "bg-green-50 text-green-700 border border-green-100" 
                : "bg-red-50 text-red-700 border border-red-100"
            }`}>
              {statusMsg.type === "success" ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
              <span>{statusMsg.text}</span>
            </div>
          )}
        </header>

        {/* CONTAINER */}
        <div className="flex-grow flex p-8 gap-6 overflow-hidden">
          {/* TICKETS LIST PANEL */}
          <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden flex flex-col w-2/5">
            {/* SEARCH & FILTER */}
            <div className="p-5 border-b border-gray-100 space-y-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search tickets..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-11 pr-5 py-3 rounded-2xl bg-gray-50 border-transparent focus:border-green-500 focus:bg-white text-sm outline-none transition-all border shadow-inner"
                />
                <Search className="absolute left-4 top-3.5 w-4 h-4 text-gray-400" />
              </div>

              <div className="flex gap-2 p-1 bg-gray-50 rounded-xl">
                {["all", "pending", "replied"].map((filter) => (
                  <button
                    key={filter}
                    onClick={() => setStatusFilter(filter)}
                    className={`flex-1 py-2 text-xs font-bold rounded-lg uppercase tracking-wider transition-all ${
                      statusFilter === filter
                        ? "bg-white text-green-600 shadow-sm"
                        : "text-gray-400 hover:text-gray-600"
                    }`}
                  >
                    {filter}
                  </button>
                ))}
              </div>
            </div>

            {/* TICKETS LIST */}
            <div className="flex-grow overflow-y-auto divide-y divide-gray-50">
              {loading ? (
                <div className="p-8 text-center text-gray-400 italic text-sm">Loading tickets...</div>
              ) : filteredTickets.length === 0 ? (
                <div className="p-12 text-center text-gray-400 space-y-4">
                  <Inbox className="w-12 h-12 mx-auto text-gray-200" />
                  <p className="text-sm italic">No support tickets found.</p>
                </div>
              ) : (
                filteredTickets.map((ticket) => (
                  <button
                    key={ticket.id}
                    onClick={() => {
                      setSelectedTicket(ticket);
                      setStatusMsg(null);
                      setReplyText("");
                    }}
                    className={`w-full p-5 text-left transition-all hover:bg-gray-50/50 flex gap-4 ${
                      selectedTicket?.id === ticket.id ? "bg-green-50/30 border-l-4 border-green-500 pl-4" : ""
                    }`}
                  >
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-600 font-bold uppercase shrink-0 text-sm">
                      {ticket.name ? ticket.name[0] : <User size={18} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-baseline mb-1">
                        <h4 className="font-bold text-gray-900 truncate text-sm">
                          {ticket.name || "Guest User"}
                        </h4>
                        <span className="text-[10px] text-gray-400 shrink-0">
                          {new Date(ticket.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-xs font-semibold text-gray-700 truncate mb-1">
                        {ticket.subject}
                      </p>
                      <p className="text-xs text-gray-400 truncate">
                        {ticket.message}
                      </p>
                    </div>
                    <div className="shrink-0 flex flex-col justify-between items-end">
                      <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${
                        ticket.status === "replied"
                          ? "bg-green-100 text-green-700"
                          : "bg-amber-100 text-amber-700"
                      }`}>
                        {ticket.status}
                      </span>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* TICKET DETAILS PANEL */}
          <div className="flex-grow bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden flex flex-col relative">
            {selectedTicket ? (
              <div className="flex-grow flex flex-col h-full overflow-hidden">
                {/* DETAIL HEADER */}
                <div className="p-6 border-b border-gray-100 bg-white z-10">
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-green-600 flex items-center justify-center text-white font-bold text-lg uppercase">
                        {selectedTicket.name ? selectedTicket.name[0] : <User size={22} />}
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900 text-base">
                          {selectedTicket.name || "Guest User"}
                        </h3>
                        <p className="text-xs text-gray-400">{selectedTicket.email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-1 ${
                        selectedTicket.status === "replied"
                          ? "bg-green-100 text-green-700"
                          : "bg-amber-100 text-amber-700"
                      }`}>
                        {selectedTicket.status}
                      </span>
                      <p className="text-[10px] text-gray-400">
                        Received: {new Date(selectedTicket.createdAt).toLocaleString()}
                      </p>
                    </div>
                  </div>

                  <div className="mt-5 pt-4 border-t border-gray-50">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-gray-400 block mb-1">Subject</span>
                    <h2 className="text-lg font-bold text-gray-900">{selectedTicket.subject}</h2>
                  </div>
                </div>

                {/* CONVERSATION AREA */}
                <div className="flex-grow overflow-y-auto p-6 space-y-6 bg-gray-50/20">
                  {/* Inquiry Message */}
                  <div className="flex justify-start">
                    <div className="max-w-[85%] bg-white p-5 rounded-2xl shadow-sm border border-gray-100 rounded-tl-none">
                      <span className="text-[10px] font-bold text-green-600 block mb-1.5 uppercase tracking-wide">
                        Inquiry Details
                      </span>
                      <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
                        {selectedTicket.message}
                      </p>
                    </div>
                  </div>

                  {/* Reply Message */}
                  {selectedTicket.status === "replied" && (
                    <div className="flex justify-end">
                      <div className="max-w-[85%] bg-green-50/80 p-5 rounded-2xl shadow-sm border border-green-100 rounded-tr-none">
                        <div className="flex justify-between items-center mb-1.5 gap-4">
                          <span className="text-[10px] font-bold text-green-700 uppercase tracking-wide">
                            Admin Response
                          </span>
                          <span className="text-[9px] text-gray-400">
                            Sent: {selectedTicket.repliedAt ? new Date(selectedTicket.repliedAt).toLocaleString() : ""}
                          </span>
                        </div>
                        <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
                          {selectedTicket.reply}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* REPLY AREA */}
                <div className="p-6 border-t border-gray-100 bg-white">
                  {/* Quick templates */}
                  <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
                    <span className="text-xs text-gray-400 font-bold uppercase shrink-0 pt-1">Quick Templates:</span>
                    {templates.map((tpl, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => insertTemplate(tpl.text)}
                        className="px-2.5 py-1 rounded-lg border border-gray-200 text-[10px] font-bold text-gray-500 bg-white hover:bg-gray-50 hover:text-green-600 hover:border-green-200 transition-all shrink-0 cursor-pointer"
                      >
                        + {tpl.label}
                      </button>
                    ))}
                  </div>

                  <form onSubmit={handleSendReply} className="flex gap-3">
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder={`Type email response to ${selectedTicket.name || "user"}...`}
                      rows="3"
                      className="flex-grow px-5 py-4 rounded-2xl bg-gray-50 border-transparent focus:border-green-500 focus:bg-white focus:ring-0 text-sm outline-none transition-all border shadow-inner resize-none"
                    ></textarea>
                    <button
                      type="submit"
                      disabled={replying || !replyText.trim()}
                      className="px-6 bg-green-600 text-white rounded-2xl hover:bg-green-700 transition disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-green-200 font-bold text-sm shrink-0 cursor-pointer"
                    >
                      <Send size={16} />
                      {replying ? "Sending..." : "Send Reply"}
                    </button>
                  </form>
                </div>
              </div>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-center p-12 space-y-4">
                <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center animate-pulse">
                  <Mail className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Select a support ticket</h3>
                <p className="text-gray-400 max-w-sm text-sm">
                  Click on an inquiry in the left pane to view message details, inspect the sender, and write email responses.
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
